import React, { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes@0.4.6';

import '@/i18n';
import '@/index.css';

import { initUrlParams } from '@/utils/urlParams';
import Bugsnag, { isBugsnagInitialized } from '@/utils/bugsnag';
import { BugsnagErrorFallback } from '@/components/BugsnagErrorFallback';
import { Toaster } from '@/components/ui/sonner';
import { AuthInitializer } from '@/components/AuthInitializer';
import { LoginPage } from '@/components/LoginPage';
import { SignupPage } from '@/components/SignupPage';
import App from '@/App';
import { PublicDashboardPage } from '@/cowork/dashboards/PublicDashboardPage';
import { PublicReportPage } from '@/cowork/reports/PublicReportPage';
import { PublicSoundPage } from '@/components/audioStudio/PublicSoundPage';
import { PublicLibraryPage } from '@/components/audioStudio/PublicLibraryPage';

initUrlParams();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

const appElement = <App />;

/**
 * Build the Bugsnag React ErrorBoundary only after `Bugsnag.start()`.
 * Returns null when reporting is disabled so the app still mounts without a boundary.
 * @see https://docs.bugsnag.com/platforms/javascript/react/
 */
function createErrorBoundary() {
  // 1. Gate on successful start() — getPlugin('react') throws if called too early
  if (!isBugsnagInitialized || !Bugsnag) {
    return null;
  }
  if (typeof Bugsnag.getPlugin !== 'function') {
    return null;
  }

  // 2. Ask the React plugin for a boundary component wired to this React copy
  try {
    const reactPlugin = Bugsnag.getPlugin('react');
    if (reactPlugin && typeof reactPlugin.createErrorBoundary === 'function') {
      return reactPlugin.createErrorBoundary(React) as React.ComponentType<{
        FallbackComponent?: React.ComponentType;
        children?: React.ReactNode;
      }>;
    }
  } catch (error) {
    console.error('Failed to create Bugsnag ErrorBoundary:', error);
    return null;
  }
  return null;
}

// The phone review page is its own chunk (the desktop pages it does not use stay out of it).
const MobileReviewPage = lazy(() => import('@/cowork/mobileReview/MobileReviewPage').then((m) => ({ default: m.MobileReviewPage })));
// Deck Studio's media picker on its own, framed by the back office's creator program editor (its own chunk too).
const MediaPickerEmbed = lazy(() => import('@/cowork/creatorPrograms/MediaPickerEmbed').then((m) => ({ default: m.MediaPickerEmbed })));
const ErrorBoundary = createErrorBoundary();

const tree = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Light only: the dark palette (deep purple ground) is not designed for the whole app yet, so the OS theme never switches it on. */}
      <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" enableSystem={false}>
        <BrowserRouter>
          <AuthInitializer>
            <Routes>
              {/* Public, no login: dashboards shared by link (validated by token on the backend). */}
              <Route path="/share/dashboard/:id" element={<PublicDashboardPage />} />
              <Route path="/share/report/:id" element={<PublicReportPage />} />
              {/* Public, no login: an Audio Studio sound shared by link (`?token=`, validated by the audio API). */}
              <Route path="/share/sound/:id" element={<PublicSoundPage />} />
              {/* A library track's public page — where creators (and old adstudios.beatly.com track links) land. */}
              <Route path="/sound/:id" element={<PublicSoundPage source="library" />} />
              {/* Public, no login: Beatly's library — the default music link on every brief (creators pick a track here). */}
              <Route path="/library" element={<PublicLibraryPage />} />
              {/* Public, no login: a campaign's pending content on the client's phone (the QR pass code is the credential). */}
              <Route path="/m/review/:code" element={<Suspense fallback={<div className="fixed inset-0 bg-black" />}><MobileReviewPage /></Suspense>} />
              {/* Back office only (staff session handed over with ?jwt=): the creator program editor's image picker. */}
              <Route path="/embed/media-picker" element={<Suspense fallback={null}><MediaPickerEmbed /></Suspense>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/connect/*" element={appElement} />
              <Route path="/verify-email/*" element={appElement} />
              <Route path="/" element={appElement} />
              <Route path="/dashboard" element={appElement} />
              <Route path="/chat" element={appElement} />
              <Route path="/cowork" element={appElement} />
              <Route path="/agents" element={appElement} />
              <Route path="/agents/:agentSection" element={appElement} />
              <Route path="/messages" element={appElement} />
              <Route path="/brand-pulse" element={appElement} />
              <Route path="/content-hub/:contentHubMediaId?" element={appElement} />
              <Route path="/creators" element={appElement} />
              <Route path="/creators/search" element={appElement} />
              <Route path="/creators/lists/ambassadors" element={appElement} />
              <Route path="/creators/creatorlist/:listId" element={appElement} />
              <Route path="/creators/:creatorId" element={appElement} />
              <Route path="/creator-lists" element={appElement} />
              <Route path="/creator-lists/:listId" element={appElement} />
              <Route path="/audience-insights" element={appElement} />
              <Route path="/trends" element={appElement} />
              <Route path="/news-monitor" element={appElement} />
              <Route path="/ad-studio" element={appElement} />
              <Route path="/ad-studio/video" element={appElement} />
              <Route path="/ad-studio/dubbing" element={appElement} />
              <Route path="/ad-studio/audio" element={appElement} />
              <Route path="/campaigns" element={appElement} />
              <Route path="/campaigns/calendar" element={appElement} />
              <Route path="/campaigns/new" element={appElement} />
              <Route path="/campaigns/:campaignId" element={appElement} />
              <Route path="/campaigns/:campaignId/results/report" element={appElement} />
              <Route path="/campaigns/:campaignId/summary/report" element={appElement} />
              <Route path="/campaigns/:campaignId/:subTab/beat/:beatId" element={appElement} />
              <Route path="/campaigns/:campaignId/:subTab" element={appElement} />
              <Route path="/ambassador-hub" element={appElement} />
              <Route path="/drafts" element={appElement} />
              <Route path="/casting" element={appElement} />
              <Route path="/strategies" element={appElement} />
              <Route path="/performance" element={appElement} />
              <Route path="/brand-lift" element={appElement} />
              <Route path="/marketing-mix-model" element={appElement} />
              <Route path="/awards" element={appElement} />
              <Route path="/awards/:trophyId" element={appElement} />
              <Route path="/settings" element={appElement} />
              <Route path="/lykke" element={appElement} />
              <Route path="/lykke/:campaignId" element={appElement} />
              <Route path="/team" element={appElement} />
              {/* Marketplace OAuth returns here; keep it inside the authenticated app shell. */}
              <Route path="/creator-marketplace" element={appElement} />
              {/* OAuth providers redirect to /ad-accounts/connect?session=… */}
              <Route path="/ad-accounts/connect" element={appElement} />
              <Route path="/ad-accounts" element={appElement} />
              <Route path="/dev-mode" element={appElement} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthInitializer>
          <Toaster position="top-center" closeButton />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);

createRoot(document.getElementById('root')!).render(
  ErrorBoundary ? (
    <ErrorBoundary FallbackComponent={BugsnagErrorFallback}>{tree}</ErrorBoundary>
  ) : (
    tree
  ),
);
